import { signIn, signOut } from "next-auth/react";
import { useQueryClient } from "@tanstack/react-query";
import { useStore } from "@store/index";
import { invalidateProfileQueries } from "@store/helpers/profile/invalidateProfileQueries";
import { getDatadogRum } from "@lib/monitoring/datadogHelper";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { useCallback } from "react";

// Track if a logout is actively in progress using a const state object (SonarQube compliant)
export const authState = {
    isSignOutInProgress: false,
};

const useSignOut = () => {
    const clearWishlist = useStore((state) => state.clearWishlist);
    const clearMe = useStore((state) => state.clearMe);
    const setHasFreshData = useStore((state) => state.setHasFreshData);
    const queryClient = useQueryClient();

    const signOutHandler = useCallback(
        async (redirectTo: string = "") => {
            authState.isSignOutInProgress = true;
            try {
                clearWishlist();
                clearMe();
                setHasFreshData(false);
                getDatadogRum()?.clearUser();
                if (redirectTo) {
                    await signOut({ callbackUrl: redirectTo });
                    return;
                }

                await signOut({ redirect: false });

                // Mint a fresh anonymous session before returning so the next user action
                // (add-to-cart, etc.) has a valid bearer. RootWrapper's effect would also
                // do this, but only after the next render — awaiting here closes the race.
                const anon = await signIn("anonymous", { redirect: false });
                if (!anon || anon.error) {
                    Logger.error(
                        ServiceType.WEB,
                        "useSignOut: anonymous sign-in after logout failed",
                        {
                            tag: LogTag.AUTHENTICATION,
                            error: anon?.error,
                        }
                    );
                }

                // In-flight cart fetches kicked off pre-logout (useFreshData.fetchActiveData,
                // recalculateCart, etc.) can resolve during the signOut window and write the cart
                // back via setActiveData/setCart. Re-clear after signOut so the basket reflects
                // the logged-out state without requiring a refresh or tab-switch.
                clearWishlist();
                clearMe();
                setHasFreshData(false);

                invalidateProfileQueries(queryClient);
            } finally {
                authState.isSignOutInProgress = false;
            }
        },
        [queryClient, clearWishlist, clearMe, setHasFreshData]
    );

    return { signOut: signOutHandler };
};

export default useSignOut;
